import { cn } from "@devhop/ui";
import { UserIcon, UserRoundIcon, UsersIcon } from "lucide-react";
import type { PrivateLessonPackageKind } from "@/modules/private-lessons/domain/contracts/templates";

type PrivateLessonPackageKindRadioProps = {
  value: PrivateLessonPackageKind;
  onChange: (value: PrivateLessonPackageKind) => void;
  onBlur?: () => void;
  name?: string;
  disabled?: boolean;
  label?: string;
};

const OPTIONS: Array<{
  value: PrivateLessonPackageKind;
  title: string;
  description: string;
  Icon: typeof UserIcon;
}> = [
  {
    value: "individual",
    title: "ສ່ວນຕົວ (1:1)",
    description: "1 ຊົ່ວໂມງຕໍ່ຫນ່ວຍລາຄາ · ເລືອກຊົ່ວໂມງຕອນຂາຍ",
    Icon: UserIcon,
  },
  {
    value: "group",
    title: "ກຸ່ມ (GROUP CLASS)",
    description: "ຊົ່ວໂມງຄົງທີ່ + ລາຄາຕໍ່ຄົນ",
    Icon: UsersIcon,
  },
  {
    value: "private_shared",
    title: "Private Package",
    description: "ຫຼາຍຄົນ · tier ລາຄາ/ຊມ. · ເລືອກຊົ່ວໂມງຕອນຂາຍ",
    Icon: UserRoundIcon,
  },
];

export function PrivateLessonPackageKindRadio({
  value,
  onChange,
  onBlur,
  name = "packageKind",
  disabled = false,
  label = "ປະເພດແມ່ແບບ",
}: PrivateLessonPackageKindRadioProps) {
  return (
    <fieldset className="min-w-0 border-0 p-0">
      <legend className="mb-2 font-medium text-sm">{label}</legend>
      <div className="grid gap-3 lg:grid-cols-3">
        {OPTIONS.map(({ value: optValue, title, description, Icon }) => {
          const selected = value === optValue;
          return (
            <label
              key={optValue}
              className={cn(
                "flex cursor-pointer gap-3 rounded-lg border p-4 text-left ring-offset-background transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-red-600 focus-within:ring-offset-2",
                disabled && "cursor-not-allowed opacity-60",
                selected
                  ? "border-red-600 bg-muted/50"
                  : "border-border hover:bg-muted/30",
              )}
            >
              <input
                type="radio"
                name={name}
                value={optValue}
                checked={selected}
                disabled={disabled}
                onChange={() => onChange(optValue)}
                onBlur={onBlur}
                className="sr-only"
              />
              <Icon
                className={cn(
                  "mt-0.5 size-5 shrink-0 stroke-[1.5]",
                  selected ? "text-red-600" : "text-muted-foreground",
                )}
                aria-hidden
              />
              <span className="min-w-0 space-y-1">
                <span className="block font-semibold text-sm leading-tight">
                  {title}
                </span>
                <span className="block text-muted-foreground text-xs leading-snug">
                  {description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
